import { DETAIL_USER_GROUP_ADD_FULFILLED, DETAIL_USER_GROUP_REMOVE_FULFILLED } from 'Modules/UserDetailModule/actionTypes'
import { GROUP_DELETE_FULFILLED, GROUP_SET_NAME_FULFILLED, GROUP_SET_PERMISSIONS_FULFILLED } from 'Global/Stores/group'
import { set } from 'Global/Helpers/objectHelpers'

const ADDITIONAL_ADVISORS_SET = 'API|AUTH/USER/ADDITIONAL_ADVISORS/SET'
export const ADDITIONAL_ADVISORS_SET_FULFILLED = `FULFILLED_${ADDITIONAL_ADVISORS_SET}`
const ADDITIONAL_ADVISORS_SET_PENDING = `PENDING_${ADDITIONAL_ADVISORS_SET}`
const ADDITIONAL_ADVISORS_SET_REJECTED = `REJECTED_${ADDITIONAL_ADVISORS_SET}`

export const DEFAULTS_SET = 'USER/AUTH/DEFAULTS/SET'

const PASSWORD_CHANGE = 'API|AUTH/USER/PASSWORD/CHANGE'
export const PASSWORD_CHANGE_FULFILLED = `FULFILLED_${PASSWORD_CHANGE}`
const PASSWORD_CHANGE_PENDING = `PENDING_${PASSWORD_CHANGE}`
const PASSWORD_CHANGE_REJECTED = `REJECTED_${PASSWORD_CHANGE}`

const SIGN_IN = 'API|AUTH/USER/SIGN_IN'
export const SIGN_IN_FULFILLED = `FULFILLED_${SIGN_IN}`
const SIGN_IN_PENDING = `PENDING_${SIGN_IN}`
const SIGN_IN_REJECTED = `REJECTED_${SIGN_IN}`

export const SIGN_IN_LS = 'AUTH/SIGN_IN_LS'

export const SIGN_OUT = 'AUTH/USER/SIGN_OUT'

const SETTING_SET = 'API|AUTH/USER/SETTING/SET'
export const SETTING_SET_FULFILLED = `FULFILLED_${SETTING_SET}`
const SETTING_SET_PENDING = `PENDING_${SETTING_SET}`
const SETTING_SET_REJECTED = `REJECTED_${SETTING_SET}`

export const TIMESTAMP_LOGIN = 'API|TIMESTAMP/LOGIN'

const UPDATE_USER_GROUPS = 'API|AUTH/USER/UPDATE_USER_GROUPS'
export const UPDATE_USER_GROUPS_FULFILLED = `FULFILLED_${UPDATE_USER_GROUPS}`

/**
 * POST request to API to set additional advisors.
 * @param {array} additionalAdvisors
 * @param {number} userId
 * @returns {Object} Action Object
 */
export const additionalAdvisorsSet = (additionalAdvisors, userId) => ({
  meta: {
    data: { additionalAdvisors, id: userId },
    method: 'patch',
    url: '/api/v1/users/setAdditionalAdvisors',
  },
  type: ADDITIONAL_ADVISORS_SET,
})

/**
 * Sets that defaults are set are not required to be set again.
 * @param {Object} user
 */
export const setDefaultsSet = () => ({
  type: DEFAULTS_SET,
})

/**
 * Signs the user in from local storage.
 * @param {Object} user
 */
export const signInFromLs = user => ({
  payload: user,
  type: SIGN_IN_LS,
})

/**
 * PATCH request to API to change password.
 * @param {string} oldPassword
 * @param {string} newPassword
 * @param {number} userId
 * @returns {Object} Action Object
 */
export const passwordChange = (oldPassword, newPassword, userId) => ({
  meta: {
    data: { id: userId, oldPassword, newPassword },
    method: 'patch',
    successMessage: 'Password successfully changed.',
    url: '/api/v1/users/changePassword',
  },
  type: PASSWORD_CHANGE,
})

/**
 * POST request to API to set setting.
 * @param {string} setting
 * @param {string} value
 * @param {Object} user
 * @returns {Object} Action Object
 */
export const settingSet = (setting, value, user) => ({
  meta: {
    data: { id: user.id, setting, value },
    method: 'post',
    url: '/api/v1/users/setSetting',
  },
  type: SETTING_SET,
})

/**
 * POST request to API to sign the user in.
 * @param {string} email
 * @param {string} password
 * @returns {Object} Action Object
 */
export const signIn = (email, password) => ({
  meta: {
    method: 'post',
    params: { email, password },
    url: '/api/v1/auth/signIn',
  },
  type: SIGN_IN,
})

/**
 * Action to sign out.
 * @returns {Object} Action Object
 */
export const signOut = () => ({
  type: SIGN_OUT,
})

/**
 * POST request to API to timestamp an login.
 * @param {Object} user
 * @returns {Object} Action Object
 */
export const timestampLogin = user => ({
  meta: {
    data: { id: user.id },
    method: 'patch',
    url: '/api/v1/users/timestampLogin',
  },
  type: TIMESTAMP_LOGIN,
})

const initialState = {
  additionalAdvisorsSet: { hasError: false, isPending: false },
  defaultsSet: false,
  passwordChange: { hasError: false, isPending: false },
  settingSet: { hasError: false, isPending: false },
  signIn: { hasError: false, isPending: false },
  user: {},
}

export const updateUserGroups = id => ({
  meta: {
    url: `/api/v1/users/${id}`,
  },
  type: UPDATE_USER_GROUPS,
})

const reducer = (state = initialState, action) => {
  const { payload, type } = action

  switch (type) {
    case ADDITIONAL_ADVISORS_SET_FULFILLED:
      return {
        ...state,
        additionalAdvisorsSet: {
          hasError: false,
          isPending: false,
        },
        user: {
          ...state.user,
          additionalAdvisors: payload.data.additionalAdvisors,
        },
      }

    case ADDITIONAL_ADVISORS_SET_PENDING:
      return set(`additionalAdvisorsSet`, { hasError: false, isPending: true }, state)

    case ADDITIONAL_ADVISORS_SET_REJECTED:
      return set(`additionalAdvisorsSet`, { hasError: true, isPending: false }, state)

    case DETAIL_USER_GROUP_ADD_FULFILLED:
    case DETAIL_USER_GROUP_REMOVE_FULFILLED:
      if (payload.data.id === state.user.id) {
        return {
          ...state,
          user: {
            ...state.user,
            userGroups: payload.data.userGroups,
          },
        }
      }

      return state

    case GROUP_DELETE_FULFILLED:
      if (!!state.user.userGroups.find(g => g.id === payload.data.id)) {
        return {
          ...state,
          user: {
            ...state.user,
            userGroups: [...state.user.userGroups.filter(g => g.id !== payload.data.id)],
          },
        }
      }

      return state

    case GROUP_SET_NAME_FULFILLED:
    case GROUP_SET_PERMISSIONS_FULFILLED:
      if (!!state.user.userGroups.find(g => g.id === payload.data.id)) {
        return {
          ...state,
          user: {
            ...state.user,
            userGroups: [...state.user.userGroups.filter(g => g.id !== payload.data.id), payload.data],
          },
        }
      }

      return state

    case PASSWORD_CHANGE_FULFILLED:
      return set(`passwordChange`, { hasError: false, isPending: false }, state)

    case PASSWORD_CHANGE_PENDING:
      return set(`passwordChange`, { hasError: false, isPending: true }, state)

    case PASSWORD_CHANGE_REJECTED:
      return set(`passwordChange`, { hasError: true, isPending: false }, state)

    case SETTING_SET_FULFILLED:
      return {
        ...state,
        settingSet: {
          hasError: false,
          isPending: false,
        },
        user: {
          ...state.user,
          settings: payload.data.userSettings,
        },
      }

    case SETTING_SET_PENDING:
      return set(`settingSet`, { hasError: false, isPending: true }, state)

    case SETTING_SET_REJECTED:
      return set(`settingSet`, { hasError: true, isPending: false }, state)

    case SIGN_IN_FULFILLED:
      return {
        ...state,
        signIn: {
          hasError: false,
          isPending: false,
        },
        user: {
          ...payload.data.user,
          token: payload.data.token,
        },
      }

    case SIGN_IN_PENDING:
      return set(`signIn`, { hasError: false, isPending: true }, state)

    case SIGN_IN_REJECTED:
      return set(`signIn`, { hasError: true, isPending: false }, state)

    case SIGN_OUT:
      return initialState

    case DEFAULTS_SET:
      return {
        ...state,
        defaultsSet: true,
      }

    case SIGN_IN_LS:
      return {
        ...state,
        user: payload,
      }

    case UPDATE_USER_GROUPS_FULFILLED:
      return set(`user.userGroups`, payload.data.userGroups, state)

    default:
      return state
  }
}

export default reducer
